import { useId, useState, type ReactNode } from "react"
import { cn } from "@/shared/lib/cn"
import { Plus } from "@/shared/ui/icons"

export interface AccordionItem {
  question: string
  answer: ReactNode
}

export interface AccordionProps {
  items: AccordionItem[]
}

export function Accordion({ items }: AccordionProps) {
  const [open, setOpen] = useState<number | null>(null)

  // useId em vez de indice cru: dois acordeoes na mesma pagina gerariam
  // `accordion-panel-0` duplicado, e o aria-controls apontaria para o painel
  // errado.
  const baseId = useId()

  const toggle = (index: number) => setOpen((prev) => (prev === index ? null : index))

  return (
    <div className="flex flex-col divide-y divide-verde-profundo/10">
      {items.map((item, index) => {
        const isOpen = open === index
        const panelId = `${baseId}-panel-${index}`
        const buttonId = `${baseId}-btn-${index}`

        return (
          <div key={item.question}>
            <button
              type="button"
              id={buttonId}
              onClick={() => toggle(index)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="w-full flex items-center justify-between gap-4 py-5 text-left text-verde-profundo font-medium text-base hover:text-verde-medio transition-colors cursor-pointer"
            >
              <span className="flex-1 leading-snug">{item.question}</span>
              <span
                aria-hidden="true"
                className={cn(
                  "shrink-0 w-6 h-6 flex items-center justify-center rounded-full border border-verde-profundo/20 transition-transform duration-300",
                  isOpen && "rotate-45",
                )}
              >
                <Plus />
              </span>
            </button>

            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              // Sem `hidden`/`display:none` aqui de proposito: os dois cortam a
              // transicao de altura. O estado e anunciado pelo aria-expanded do
              // botao, que e o padrao de disclosure.
              className={cn("overflow-hidden transition-all duration-300", isOpen ? "max-h-96 pb-5" : "max-h-0")}
            >
              <p className="text-verde-profundo/75 text-base leading-relaxed">{item.answer}</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
